/**
 * Shared UI kit.
 *
 * One set of components for learner, admin and superadmin. They read only tokens,
 * never literal colours, so the same Button is tenant-teal on a learner page and
 * console-orange in the platform console with no conditional code.
 *
 * If a component needs a colour that isn't a token, the token is missing — add it
 * to tokens.css rather than reaching for a hex value here.
 */

import { useEffect, useRef } from 'react';
import type {
  ButtonHTMLAttributes,
  InputHTMLAttributes,
  ReactNode,
  SelectHTMLAttributes,
  TextareaHTMLAttributes,
} from 'react';
import './ui.css';

// Prototype-derived components live alongside these and are re-exported so a page
// imports everything from '../../shared/ui'.
export * from './prototype';
// Used by Dialog's close button; imported explicitly because `export *` re-exports a
// name without binding it locally.
import { Icon } from './prototype';

type Variant = 'primary' | 'secondary' | 'ghost' | 'danger';

export function Button({
  variant = 'secondary',
  busy = false,
  className,
  children,
  ...rest
}: ButtonHTMLAttributes<HTMLButtonElement> & { variant?: Variant; busy?: boolean }) {
  return (
    <button
      // `className` is pulled out of `rest` and MERGED, not spread over.
      //
      // It used to be `className="ui-btn"` followed by `{...rest}`, so any caller passing a
      // className replaced `ui-btn` outright and got an unstyled button — which is exactly
      // what happened to the learner Enrol button. The bug is invisible at the call site:
      // adding a modifier class silently removes the base one.
      className={className ? `ui-btn ${className}` : 'ui-btn'}
      data-variant={variant}
      // Disable while busy so a slow write can't be submitted twice — with tenant
      // creation that would mean two half-provisioned tenants.
      disabled={busy || rest.disabled}
      aria-busy={busy || undefined}
      {...rest}
    >
      {busy ? 'Working…' : children}
    </button>
  );
}

export function Field({
  label,
  hint,
  error,
  ...rest
}: InputHTMLAttributes<HTMLInputElement> & { label: string; hint?: string; error?: string }) {
  const id = rest.id ?? rest.name;
  const describedBy = [hint && `${id}-hint`, error && `${id}-error`].filter(Boolean).join(' ');

  return (
    <div className="ui-field" data-invalid={error ? 'true' : undefined}>
      <label htmlFor={id}>{label}</label>
      <input
        id={id}
        aria-invalid={error ? true : undefined}
        aria-describedby={describedBy || undefined}
        {...rest}
      />
      {hint && !error && (
        <p className="ui-field__hint" id={`${id}-hint`}>
          {hint}
        </p>
      )}
      {/* Errors explain what to change, never just "invalid". */}
      {error && (
        <p className="ui-field__error" id={`${id}-error`} role="alert">
          {error}
        </p>
      )}
    </div>
  );
}

/**
 * The multi-line sibling of `Field`, with the same label/hint/error wiring.
 *
 * It exists because there was no textarea component at all: every long-text input in this
 * tree hand-rolls one inside a `<label className="admin-field">`, and `admin/enrolments/
 * enrolments.css` documents copying declarations to make one of them look right. That is
 * three copies of a control whose accessibility wiring is the part worth sharing —
 * `aria-invalid`, `aria-describedby` and a `role="alert"` error are exactly what a
 * hand-rolled one leaves out.
 *
 * Prompted by the refund screens, where the free-text reason is REQUIRED on every
 * destructive action and is the field an operator is most likely to get wrong. A validation
 * message a screen reader never announces is a validation message that does not exist.
 */
export function TextAreaField({
  label,
  hint,
  error,
  ...rest
}: TextareaHTMLAttributes<HTMLTextAreaElement> & {
  label: string;
  hint?: string;
  error?: string;
}) {
  const id = rest.id ?? rest.name;
  const describedBy = [hint && `${id}-hint`, error && `${id}-error`].filter(Boolean).join(' ');

  return (
    <div className="ui-field" data-invalid={error ? 'true' : undefined}>
      <label htmlFor={id}>{label}</label>
      <textarea
        id={id}
        aria-invalid={error ? true : undefined}
        aria-describedby={describedBy || undefined}
        {...rest}
      />
      {hint && !error && (
        <p className="ui-field__hint" id={`${id}-hint`}>
          {hint}
        </p>
      )}
      {error && (
        <p className="ui-field__error" id={`${id}-error`} role="alert">
          {error}
        </p>
      )}
    </div>
  );
}

export function SelectField({
  label,
  options,
  ...rest
}: SelectHTMLAttributes<HTMLSelectElement> & { label: string; options: [string, string][] }) {
  const id = rest.id ?? rest.name;
  return (
    <div className="ui-field">
      <label htmlFor={id}>{label}</label>
      <select id={id} {...rest}>
        {options.map(([value, text]) => (
          <option key={value} value={value}>
            {text}
          </option>
        ))}
      </select>
    </div>
  );
}

export function Card({ title, action, children }: { title?: string; action?: ReactNode; children: ReactNode }) {
  return (
    <section className="ui-card">
      {(title || action) && (
        <header className="ui-card__head">
          {title && <h2>{title}</h2>}
          {action}
        </header>
      )}
      <div className="ui-card__body">{children}</div>
    </section>
  );
}

/** Status in form as well as text, so what needs attention reads at a glance. */
export function Pill({ tone, children }: { tone: 'active' | 'planned' | 'suspended' | 'retired' | 'neutral'; children: ReactNode }) {
  return (
    <span className="ui-pill" data-tone={tone}>
      {children}
    </span>
  );
}

export function Banner({ tone, children }: { tone: 'info' | 'warning' | 'danger' | 'success'; children: ReactNode }) {
  return (
    <div className="ui-banner" data-tone={tone} role={tone === 'danger' ? 'alert' : 'status'}>
      {children}
    </div>
  );
}

export function Stat({ label, value, tone }: { label: string; value: string | number; tone?: 'warning' | 'danger' }) {
  return (
    <div className="ui-stat" data-tone={tone}>
      <b>{value}</b>
      <i>{label}</i>
    </div>
  );
}

export function EmptyState({ title, body, action }: { title: string; body: string; action?: ReactNode }) {
  return (
    <div className="ui-empty">
      <h3>{title}</h3>
      <p>{body}</p>
      {action}
    </div>
  );
}

export function Spinner({ label }: { label: string }) {
  return (
    <p className="ui-spinner" role="status">
      {label}
    </p>
  );
}

/**
 * Modal dialog, built on the native `<dialog>` element.
 *
 * Native rather than a div-with-a-backdrop because the platform gives us the three things
 * hand-rolled modals almost always get wrong: focus is trapped inside while open, Escape
 * closes it, and everything behind it is inert to both pointer and screen reader. It also
 * renders in the top layer, so it cannot be clipped by an ancestor's `overflow` — which is
 * exactly what happens to an absolutely-positioned modal inside a scrolling table.
 */
export function Dialog({
  title,
  onClose,
  children,
  footer,
  wide = false,
}: {
  title: string;
  onClose: () => void;
  children: ReactNode;
  footer?: ReactNode;
  wide?: boolean;
}) {
  const ref = useRef<HTMLDialogElement>(null);

  useEffect(() => {
    // showModal() cannot be set declaratively, and calling it on an already-open dialog
    // throws, so it is guarded rather than called on every render.
    const dialog = ref.current;
    if (dialog && !dialog.open) dialog.showModal();
  }, []);

  return (
    <dialog
      className="ui-dialog"
      data-wide={wide || undefined}
      ref={ref}
      // Fires for Escape and for the close button alike, so there is one exit path.
      onClose={onClose}
      // The backdrop is part of the dialog element, so a click landing on the element
      // itself rather than on its content box is a click outside.
      onClick={(event) => {
        if (event.target === ref.current) ref.current?.close();
      }}
    >
      <div className="ui-dialog__inner">
        <header className="ui-dialog__head">
          <h2>{title}</h2>
          <button
            type="button"
            className="ui-dialog__close"
            onClick={() => ref.current?.close()}
            aria-label="Close"
          >
            <Icon name="close" />
          </button>
        </header>
        <div className="ui-dialog__body">{children}</div>
        {footer && <footer className="ui-dialog__foot">{footer}</footer>}
      </div>
    </dialog>
  );
}

/**
 * Confirmation for an action that destroys work.
 *
 * Takes the item's name and repeats it in the body, because "Are you sure?" on its own is
 * the reason people delete the wrong thing — the dialog has to say what is about to go.
 */
export function ConfirmDialog({
  title,
  body,
  confirmLabel,
  onConfirm,
  onClose,
  busy = false,
}: {
  title: string;
  body: ReactNode;
  confirmLabel: string;
  onConfirm: () => void;
  onClose: () => void;
  busy?: boolean;
}) {
  return (
    <Dialog
      title={title}
      onClose={onClose}
      footer={
        <>
          <Button variant="danger" busy={busy} onClick={onConfirm}>
            {confirmLabel}
          </Button>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
        </>
      }
    >
      {body}
    </Dialog>
  );
}
